import { Injectable } from '@angular/core';
import { AnalyticsContext } from '@core/services/core/analytics/analytics-context';
import { AnalyticsInitializer } from '@core/services/core/analytics/analytics-initializer';
import {
  ActionSheetController,
  AlertController,
  LoadingController,
  ModalController,
  Platform
} from '@ionic/angular';
import { AlertButton, AlertOptions, ComponentProps, ComponentRef } from '@ionic/core';
import { ActionSheetOptions } from '@ionic/core/dist/types/components/action-sheet/action-sheet-interface';
import { PageAnalytics } from '@models/page-analytics';
import { PresentAlertOptions } from '@models/ui';
import { TranslateService } from '@ngx-translate/core';
import { ContextService } from './context.service';
import { LogService } from './log.service';

export interface CustomElementParams {
  message?: string;
  className?: string;
  interpolate?: object;
  src?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UiHelperService {
  private loadings = new Set();
  private alert: HTMLIonAlertElement;

  constructor(
    private trans: TranslateService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private platform: Platform,
    private ctx: ContextService,
    private log: LogService,
    private actionSheetController: ActionSheetController,
    private modalController: ModalController,
    private analyticsCtx: AnalyticsContext,
    private analyticsInitializer: AnalyticsInitializer
  ) {}

  closeAlert() {
    if (this.alert) {
      this.alert.dismiss();
    }
  }

  async presentLoading(pageId: string) {
    if (this.loadings.has(pageId)) {
      this.log.w(
        this,
        'Calling multiple times from same page to present loading'
      );
      return;
    }
    this.loadings.add(pageId);
    if (this.loadings.size !== 1) return;

    const loading = await this.loadingController.create({
      spinner: 'crescent',
      translucent: true,
      mode: this.ctx.isIOSApp ? 'ios' : 'md'
    });

    loading.present();
  }

  dismissLoading(pageId: string) {
    this.loadings.delete(pageId);
    if (this.loadings.size === 0) {
      this.loadingController.dismiss().catch(() => {});
    }
  }

  // Alerts
  async presentAlert(
    message: string,
    title?: string,
    subtitle?: string,
    buttonText?: string,
    onSuccess?: () => void,
    alertExtraOptions: any = {},
    analytics?: PageAnalytics
  ) {
    const alert = await this.alertController.create({
      header: title ? this.trans.instant(title) : null,
      subHeader: subtitle ? this.trans.instant(subtitle) : null,
      message: message ? this.trans.instant(message) : null,
      translucent: true,
      cssClass: 'simple-alert',
      buttons: [
        {
          text: buttonText
            ? this.trans.instant(buttonText)
            : this.trans.instant('ACCEPT'),
          handler: () => {
            if (onSuccess != null) {
              onSuccess();
            }
          }
        }
      ],
      backdropDismiss: false,
      ...alertExtraOptions
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
  }

  async presentMessageAlert({
    message,
    interpolateParams,
    analytics
  }: {
    message: string;
    interpolateParams?: Object;
    analytics?: PageAnalytics;
  }) {
    const alert = await this.alertController.create({
      message: this.trans.instant(message || '', interpolateParams),
      translucent: true,
      buttons: [
        {
          text: this.trans.instant('ACCEPT')
        }
      ],
      backdropDismiss: false
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
  }

  async presentCustomAlertModalInfo(
    message: string,
    title?: string,
    onClose?: () => void,
    closeButtonText?: string,
    enableBackdropDismiss = false,
    isLifeCycle?: boolean,
    analytics?: PageAnalytics
  ) {
    const header = title ? title : null;
    const content = message ? message : null;
    const lifeCycle = isLifeCycle ? isLifeCycle : false;
    let alert: HTMLIonAlertElement;
    if (lifeCycle) {
      alert = await this.alertController.create({
        message:
          (header !== null
            ? `<div class="header-with-icon">${header}</div>`
            : '') +
          (content !== null
            ? `<div class="content content-margin-top mapfre-fs-xs-r">${content}</div>`
            : ''),
        mode: 'md',
        backdropDismiss: enableBackdropDismiss,
        cssClass: 'custom-alert-modal-full-screen',
        buttons: [
          {
            text: closeButtonText ? closeButtonText : this.trans.instant('CLOSE'),
            handler: () => {
              if (onClose != null) {
                onClose();
              }
            }
          }
        ]
      });
    } else {
      alert = await this.alertController.create({
        message:
          (header !== null ? `<div class="header">${header}</div>` : '') +
          (content !== null
            ? `<div class="content">${content}</div><div class="content-separator"></div>`
            : ''),
        mode: 'md',
        backdropDismiss: enableBackdropDismiss,
        cssClass: 'custom-alert-modal-full-screen',
        buttons: [{
          text: closeButtonText ? closeButtonText : this.trans.instant('CLOSE'),
          handler: () => onClose && onClose()
        }]
      });
    }

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
  }

  async presentCustomAlert(
    title?: string,
    description?: string,
    image?: string,
    onSuccess?: () => void,
    onCancel?: () => void,
    okText?: string,
    cancelText?: string,
    analytics?: PageAnalytics
  ) {
    const alert = await this.alertController.create({
      message:
        (image !== null ? `<img class="image" src="${image}">` : '') +
        (description !== null ? `<p class="desc">${description}</p>` : '') +
        (title !== null ? `<p class="title">${title}</p>` : ''),
      mode: 'md',
      backdropDismiss: false,
      cssClass: 'custom-alert-message',
      buttons: [
        {
          text: okText
            ? this.trans.instant(okText)
            : this.trans.instant('CONFIRM'),
          handler: () => {
            if (onSuccess != null) {
              onSuccess();
            }
          }
        },
        {
          text: cancelText
            ? this.trans.instant(cancelText)
            : this.trans.instant('CANCEL'),
          cssClass: 'secondary',
          handler: () => {
            if (onCancel != null) {
              onCancel();
            }
          }
        }
      ]
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
  }

  async presentBigCustomAlertTwoButtons(
    title: string,
    message: string,
    onSuccess?: () => void,
    onCancel?: () => void,
    okText?: string,
    cancelText?: string,
    isDisabled?: boolean,
    analytics?: PageAnalytics,
    customCssClass?: string
  ) {
    const alert = await this.alertController.create({
      header: title,
      message: `<div class="desc mapfre-fs-xs-r medium-dark">${message}</div`,
      translucent: true,
      mode: 'md',
      backdropDismiss: false,
      cssClass: `big-custom-alert ${customCssClass}`,
      buttons: [
        {
          text: cancelText
            ? this.trans.instant(cancelText)
            : this.trans.instant('CANCEL'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            if (onCancel != null) {
              onCancel();
            }
          }
        },
        {
          text: okText
            ? this.trans.instant(okText)
            : this.trans.instant('CONFIRM'),
          cssClass: isDisabled ? 'disabled' : '',
          handler: () => {
            if (onSuccess != null) {
              onSuccess();
            }
          }
        }
      ]
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
  }

  async presentOneButtonCustomAlert(
    title?: string,
    description?: string,
    image?: string,
    onSuccess?: () => void,
    okText?: string,
    analytics?: PageAnalytics
  ) {
    const alert = await this.alertController.create({
      message:
        (image !== null ? `<img class="image" src="${image}">` : '') +
        (description !== null
          ? `<p class="desc">${this.trans.instant(description)}</p>`
          : '') +
        (title !== null ? `<p class="title">${title}</p>` : ''),
      mode: 'md',
      backdropDismiss: false,
      cssClass: 'custom-alert-message',
      buttons: [
        {
          text: okText
            ? this.trans.instant(okText)
            : this.trans.instant('CONFIRM'),
          handler: () => {
            if (onSuccess != null) {
              onSuccess();
            }
          }
        }
      ]
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
  }

  async presentProfileCustomAlert(
    title: string,
    image: string,
    description1: string,
    description2?: string,
    onSuccess?: () => void,
    successText?: string,
    onCancel?: () => void,
    cancelText?: string,
    cssClass?: string[],
    analytics?: PageAnalytics
  ) {
    const buttons = [];
    const successButton = {
      text: successText
        ? this.trans.instant(successText)
        : this.trans.instant('ACCEPT'),
      handler: () => {
        if (onSuccess != null) {
          onSuccess();
        }
      },
      cssClass: 'primary'
    };
    const cancelButton = {
      text: cancelText
        ? this.trans.instant(cancelText)
        : this.trans.instant('ACCEPT'),
      role: 'cancel',
      cssClass: 'secondary',
      handler: () => {
        if (onCancel != null) {
          onCancel();
        }
      }
    };

    if (cancelText || onCancel) {
      buttons.push(cancelButton);
    }
    if (successText || onSuccess) {
      buttons.push(successButton);
    }

    const alert = await this.alertController.create({
      message:
        (image !== null ? `<img class="image" src="${image}">` : '') +
        (title !== null ? `<p class="title">${title}</p>` : '') +
        (description1 !== null ? `<p class="desc">${description1}</p>` : '') +
        (description2 !== null ? `<p class="desc2">${description2}</p>` : ''),
      mode: 'md',
      backdropDismiss: false,
      cssClass: ['custom-profile-alert-message', ...(cssClass || [])],
      buttons
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
  }

  async presentDialogNew({
    title,
    message,
    onSuccess,
    onCancel,
    okButtonText,
    cancelButtonText,
    interpolateParams,
    analytics
  }: {
    title?: string;
    message?: string;
    onSuccess?: () => void;
    onCancel?: () => void;
    okButtonText?: string;
    cancelButtonText?: string;
    interpolateParams?: {};
    analytics?: PageAnalytics;
  }) {
    return this.presentDialog(
      title,
      message,
      onSuccess,
      onCancel,
      okButtonText,
      cancelButtonText,
      interpolateParams,
      analytics
    );
  }

  async presentDialog(
    title?: string,
    message?: string,
    onSuccess?: () => void,
    onCancel?: () => void,
    okButtonText?: string,
    cancelButtonText?: string,
    interpolateParams?: {},
    analytics?: PageAnalytics
  ) {
    const alert = await this.alertController.create({
      header: title ? this.trans.instant(title, interpolateParams) : null,
      message: message ? this.trans.instant(message, interpolateParams) : null,
      translucent: true,
      backdropDismiss: false,
      buttons: [
        {
          text: cancelButtonText
            ? this.trans.instant(cancelButtonText, interpolateParams)
            : this.trans.instant('CANCEL'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            if (onCancel != null) {
              onCancel();
            }
          }
        },
        {
          text: okButtonText
            ? this.trans.instant(okButtonText, interpolateParams)
            : this.trans.instant('ACCEPT'),
          handler: () => {
            if (onSuccess != null) {
              onSuccess();
            }
          }
        }
      ]
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
    return alert;
  }

  async presentDialogOneButton(
    title?: string,
    message?: string,
    onSuccess?: () => void,
    okButtonText?: string,
    cssClass?: string[],
    interpolateParams?: {},
    analytics?: PageAnalytics
  ) {
    const alert = await this.alertController.create({
      cssClass,
      header: title ? this.trans.instant(title, interpolateParams) : null,
      message: message ? this.trans.instant(message, interpolateParams) : null,
      translucent: true,
      backdropDismiss: false,
      buttons: [
        {
          text: okButtonText
            ? this.trans.instant(okButtonText)
            : this.trans.instant('ACCEPT'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            if (onSuccess != null) {
              onSuccess();
            }
          }
        }
      ]
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
    return alert;
  }

  private trackAnalyticsNavigation({
    analytics,
    alert
  }: {
    analytics: PageAnalytics;
    alert: HTMLIonAlertElement;
  }) {
    const { slug } = { ...analytics };
    if (slug) {
      this.analyticsCtx.setModalTitle({ slug });
      this.analyticsInitializer.triggerManualNavigation();
      alert.onDidDismiss().then(() => this.analyticsCtx.clearModalTitle());
    }
  }

  async presentDialogOneButtonScrollable(
    title?: string,
    message?: string,
    onSuccess?: () => void,
    okButtonText?: string,
    cssClass?: string[]
  ) {
    const alert = await this.alertController.create({
      cssClass,
      header: title ? this.trans.instant(title) : null,
      message: message
        ? `<ion-content>${this.trans.instant(message)}</ion-content>`
        : null,
      translucent: true,
      backdropDismiss: false,
      buttons: [
        {
          text: okButtonText
            ? this.trans.instant(okButtonText)
            : this.trans.instant('ACCEPT'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            if (onSuccess != null) {
              onSuccess();
            }
          }
        }
      ]
    });
    await alert.present();
    this.alert = alert;
    return alert;
  }

  public async presentDialogImageAndTwoButtons(
    title?: string,
    message?: string,
    image?: string,
    onSuccess?: () => void,
    onSuccessText?: string,
    onCancel?: () => void,
    onCancelText?: string,
    interpolateParams?: {},
    cssClass?: string
  ) {
    const alert = await this.alertController.create({
      cssClass,
      message:
        (`<img class="image" src="${image}">`) +
        (`<p class="title mapfre-fw-500">${this.trans.instant(title, interpolateParams)}</p>`) +
        (`<p class="desc">${this.trans.instant(message, interpolateParams)}</p>`),
      mode: 'md',
      backdropDismiss: false,
      buttons: [
        {
          text: onSuccessText ? this.trans.instant(onSuccessText) : this.trans.instant('ACCEPT'),
          handler: () => {
            if (onSuccess != null) { onSuccess(); }
          },
        },
        {
          text: onCancelText ? this.trans.instant(onCancelText) : this.trans.instant('CANCEL'),
          role: 'cancel',
          cssClass: 'tertiary',
          handler: () => {
            if (onCancel != null) { onCancel(); }
          }
        },
      ],
    });
    await alert.present();
  }

  async presentCustomRadioDialog(
    title?: string,
    inputs?: { label: string; id: string; textWrap?: boolean }[],
    onSuccess?: (activeRadioName?: string) => void,
    onCancel?: () => void,
    okButtonText?: string,
    cancelButtonText?: string,
    enableBackdropDismiss?: boolean
  ) {
    let itemList = '';
    if (inputs && inputs.length) {
      await this.platform.ready();
      inputs.forEach(radioInput => {
        if (radioInput.id) {
          if (this.platform.is('ios')) {
            itemList += `
            <ion-item>
              <ion-label ${
                radioInput.textWrap ? 'class="ion-text-wrap"' : ''
              }>${radioInput.label}</ion-label>
              <ion-radio slot="end" name="${radioInput.id}" value="${
              radioInput.id
            }"></ion-radio>
            </ion-item>
          `;
          } else {
            itemList += `
            <ion-item>
              <ion-radio slot="start" name="${radioInput.id}" value="${
              radioInput.id
            }"></ion-radio>
              <ion-label ${
                radioInput.textWrap ? 'class="ion-text-wrap"' : ''
              }>${radioInput.label}</ion-label>
            </ion-item>
          `;
          }
        }
      });
    }
    const message = `
        <ion-radio-group id="customDialogRadioGroup">
          ${itemList}
        </ion-radio-group>
      `;
    const alert = await this.alertController.create({
      header: title ? this.trans.instant(title) : null,
      message: inputs && inputs.length ? message : null,
      translucent: false,
      cssClass: 'custom-radio-dialog',
      backdropDismiss: enableBackdropDismiss,
      buttons: [
        {
          text: cancelButtonText
            ? this.trans.instant(cancelButtonText)
            : this.trans.instant('CANCEL'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            if (onCancel != null) {
              onCancel();
            }
          }
        },
        {
          text: okButtonText
            ? this.trans.instant(okButtonText)
            : this.trans.instant('ACCEPT'),
          handler: () => {
            const dialogRadioGroup = document.getElementById(
              'customDialogRadioGroup'
            );
            const radioList = dialogRadioGroup
              ? dialogRadioGroup.querySelectorAll('ion-radio')
              : null;
            let activeRadioName;
            if (radioList && radioList.length) {
              radioList.forEach(radioItem => {
                if (radioItem.getAttribute('aria-checked') === 'true') {
                  activeRadioName = radioItem.getAttribute('name');
                }
              });
            }
            if (onSuccess != null) {
              onSuccess(activeRadioName);
            }
          }
        }
      ]
    });
    this.alert = alert;
    return await alert.present();
  }

  async presentOneButtonAlert({
    image,
    title,
    description,
    subdescription,
    onSuccess,
    okText,
    cssClass,
    mode,
    analytics
  }: {
    image?;
    title?;
    description?;
    subdescription?;
    onSuccess?;
    okText?;
    cssClass?;
    mode?;
    analytics?: PageAnalytics;
  }) {
    const alert = await this.alertController.create({
      message:
        (image ? `<img class="image" src="${image}">` : '') +
        (title ? `<p class="title">${this.trans.instant(title)}</p>` : '') +
        (description
          ? `<p class="desc">${this.trans.instant(description)}</p>`
          : '') +
        (subdescription
          ? `<p class="subdesc">${this.trans.instant(subdescription)}</p>`
          : ''),
      mode: mode || 'md',
      backdropDismiss: false,
      cssClass: ['custom-one-button-alert', ...(cssClass || [])],
      buttons: [
        {
          text: okText
            ? this.trans.instant(okText)
            : this.trans.instant('UNDERSTOOD'),
          handler: () => {
            if (onSuccess != null) {
              onSuccess();
            }
          }
        }
      ]
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
  }

  async presentActionSheet({ header, buttons }: ActionSheetOptions) {
    const translatedButtons = buttons.map(btn =>
      typeof btn === 'object'
        ? {
            ...btn,
            text: this.trans.instant(btn.text || '')
          }
        : this.trans.instant(btn || '')
    );
    const actionSheet = await this.actionSheetController.create({
      header: this.trans.instant(header),
      buttons: translatedButtons,
      translucent: true
    });
    await actionSheet.present();
    return actionSheet;
  }

  async previewImage(base64: string) {
    const alert = await this.alertController.create({
      message: `<img id="custom-preview-image" alt="preview" src='${base64}'/>`,
      cssClass: 'preview-image',
      translucent: true,
      backdropDismiss: true
    });

    const img = document.getElementById('custom-preview-image');
    img.addEventListener('click', () => alert.dismiss());

    await alert.present();
    this.alert = alert;
    return alert;
  }

  async presentCustomizableAlert({
    title,
    subtitle,
    message,
    image,
    cssClass,
    backdropDismiss = false,
    confirmButton,
    cancelButton,
    html
  }: Partial<PresentAlertOptions>) {
    const buttons = [];
    if (confirmButton?.show) {
      buttons.push({
        cssClass: confirmButton.cssClass,
        text: confirmButton.text ? confirmButton.text : this.trans.instant('CONFIRM'),
        handler: () => {
          if (confirmButton.onSuccess) {
            confirmButton.onSuccess();
          }
        }
      });
    }
    if (cancelButton?.show) {
      buttons.push({
        cssClass: cancelButton.cssClass,
        text: cancelButton.text ? cancelButton.text : this.trans.instant('CANCEL'),
        handler: () => {
          if (cancelButton.onCancel) {
            cancelButton.onCancel();
          }
        }
      });
    }
    const alert = await this.alertController.create({
      message: html
        ? html
        : (image?.show
            ? `<div class="img-container"><img class="image" alt="${image.alt}" src="${image.src}"></div>`
            : '') +
          (title ? `<p class="${title.cssClass}">${title.text}</p>` : '') +
          (subtitle
            ? `<p class="${subtitle.cssClass}">${subtitle.text}</p>`
            : '') +
          (message ? `<p class="${message.cssClass}">${message.text}</p>` : ''),
      mode: 'md',
      backdropDismiss,
      cssClass: Array.isArray(cssClass) ? cssClass : [cssClass],
      buttons
    });
    await alert.present();
    this.alert = alert;
  }

  async buildAlertWithParameters({
    data,
    mode,
    backdropDismiss,
    cssClass,
    buttons,
    analytics
  }) {

    const messageData = this.buildMessage(data);
    const alert = await this.alertController.create({
      message: messageData,
      mode,
      backdropDismiss,
      cssClass,
      buttons
    });

    this.trackAnalyticsNavigation({ analytics, alert });

    await alert.present();
    this.alert = alert;
  }

  private buildMessage(data: any): string {
    let messageData = '';
    for (const d of data) {
      const messageObj = {
        image: `<img class="${d.class || 'image'}" src="${d.value}">`,
        title: `<p class="${d.class || 'title'}">${this.trans.instant(d.value)}</p>`,
        description: `<p class="${d.class || 'desc'}">${this.trans.instant(d.value)}</p>`,
        subdescription: `<p class="${d.class || 'subdesc'}">${this.trans.instant(d.value)}</p>`,
        numRef: `<p class="${d.class || 'text-ref'}">Nº de referencia</p> <p class="${d.class || 'num-ref'}">${d.value}</p>`,
        info: this.buildInfo(d.value)
      };

      messageData += messageObj[d.type];
    }

    return messageData;
  }

  buildInfo(value: string[]): string {
    let info = `
    <div class="conditions-info">
      <div class="icon">
        <img src="assets/images/information-circle.svg"
          alt="info" />
      </div>
      <div class="info">`;
    for (const v of value) {
      info += `<p>${v}</p>`;
    }

    info += `
      </div>
    </div>`;

    return info;
  }

  async openComponentAsModal(
    componentRef: ComponentRef,
    componentProps?: ComponentProps,
    cssClass?: string,
    backdropDismiss = true
  ) {
    const modal = await this.modalController.create({
      component: componentRef,
      backdropDismiss,
      componentProps,
      cssClass
    });
    await modal.present();

    return modal;
  }

  async buildPresentAlert(alertOptions: AlertOptions) {
    const {
      cssClass = 'custom-alert-default',
      mode = 'md',
      ...restAlertOptions
    } = alertOptions;

    const alert = await this.alertController.create({
      cssClass,
      mode,
      ...restAlertOptions
    });

    await alert.present();
  }

  getElement(tag: keyof HTMLElementTagNameMap, params?: CustomElementParams) {
    const element: any = document.createElement(tag);

    if (params?.message) {
      const message = this.translateText(params?.message, params?.interpolate);
      element.innerHTML = message ? message : params.message;
    }
    if (params?.className) {
      element.classList.add(params?.className);
    }
    if (params?.src) {
      element.src = params.src;
    }

    return element.outerHTML;
  }

  getAlertButton(alertButton: AlertButton, className = 'alert-button'): AlertButton {
    const {
      cssClass = className,
      role = 'cancel',
      handler = () => {}
    } = alertButton;

    return { text: this.translateText(alertButton.text), cssClass, role, handler };
  }

  translateText(text: string, interpolateParams?): string {
    return this.trans.instant(text, interpolateParams);
  }

}
